import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import PhotoBriefPlaceholder from "./PhotoBriefPlaceholder";
import { PHOTO_BRIEFS } from "../data/photoBriefs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real commissioned photography lives here once uploaded via Admin Media Kit.
// Until then, every service renders the editorial Photography Brief placeholder.
const REAL_IMAGES = {
  // e.g. "window-cleaning": "https://customer-assets.emergentagent.com/...",
};

const COMING_SOON = [
  { id: "pest-control", name: "Commercial Pest Control", tagline: "Discreet, scheduled, insured." },
];

function ServiceCard({ s, i, realImage }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 180, damping: 22 });
  const smy = useSpring(my, { stiffness: 180, damping: 22 });
  const rx = useTransform(smy, [-40, 40], [4, -4]);
  const ry = useTransform(smx, [-40, 40], [-4, 4]);
  const lightX = useTransform(smx, [-40, 40], ["30%", "70%"]);
  const lightY = useTransform(smy, [-40, 40], ["30%", "70%"]);

  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 80);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 80);
  }
  function onLeave() { mx.set(0); my.set(0); }

  const image = realImage || REAL_IMAGES[s.id];
  const brief = PHOTO_BRIEFS[s.id];
  const comingSoon = s._comingSoon || s.id === "pest-control";

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className="glass-card glass-reflect float-lift group relative overflow-hidden"
      data-testid={`svc-card-${s.id}`}
      data-cursor="hover"
      data-cursor-label="Open"
    >
      {/* Background image OR photography brief placeholder */}
      {image ? (
        <motion.div
          className="absolute inset-0"
          style={{ scale: 1.05 }}
          whileHover={{ scale: 1.12 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <img src={image} alt={s.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" style={{ filter: "brightness(0.4) contrast(1.05) saturate(0.8)" }} />
        </motion.div>
      ) : (
        <PhotoBriefPlaceholder brief={brief} serviceName={s.name} />
      )}

      {/* Depth gradient overlay (only when there's a photo) */}
      {image && (
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(180deg, rgba(11,11,13,0.15) 0%, rgba(11,11,13,0.55) 45%, rgba(11,11,13,0.94) 100%)",
        }} />
      )}

      {/* Cursor-following light */}
      <motion.div
        aria-hidden
        className="absolute pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          left: lightX, top: lightY,
          width: 320, height: 320,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(198,168,124,0.28), transparent 60%)",
          filter: "blur(24px)",
          mixBlendMode: "screen",
        }}
      />

      {/* Content */}
      <div className="relative p-8 md:p-10 flex flex-col justify-between min-h-[440px] md:min-h-[520px]">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--gold)" }}>
            N° {String(i + 1).padStart(2, "0")}
          </div>
          {comingSoon ? (
            <div className="pill" style={{ background: "rgba(198,168,124,0.14)", color: "var(--gold-2)" }}>Coming Soon</div>
          ) : (
            <div
              className="w-10 h-10 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-all duration-500"
              style={{
                borderRadius: "var(--r-full)",
                border: "1px solid var(--line-gold)",
                color: "var(--gold)",
              }}
            >
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </div>
          )}
        </div>

        <div>
          <h3 className="font-display text-3xl md:text-4xl leading-[0.98]" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>
            {s.name}
          </h3>
          <p className="mt-4 font-body text-sm md:text-base max-w-md" style={{ color: "var(--text-2)", lineHeight: 1.55 }}>
            {s.tagline}
          </p>
          {comingSoon ? (
            <div
              className="mt-8 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em]"
              style={{ color: "var(--text-3)" }}
              data-testid={`svc-card-comingsoon-${s.id}`}
            >
              <span className="w-8 h-px" style={{ background: "var(--text-3)" }} />
              Notify me at launch
            </div>
          ) : (
            <Link
              to="/services"
              className="mt-8 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em] transition-colors duration-500"
              style={{ color: "var(--text-2)" }}
              data-testid={`svc-card-link-${s.id}`}
            >
              <span className="w-8 h-px transition-all duration-500 group-hover:w-12" style={{ background: "var(--gold)" }} />
              Open file
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function ServiceCards() {
  const [services, setServices] = useState([]);
  const [media, setMedia] = useState({});

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      const live = (r.data.services || []).map((s) => ({ ...s, _comingSoon: false }));
      const soon = COMING_SOON.map((s) => ({ ...s, _comingSoon: true }));
      setServices([...live, ...soon]);
    });
    axios.get(`${API}/media/services`).then((r) => {
      const raw = r.data.media || {};
      const base = process.env.REACT_APP_BACKEND_URL;
      const absolute = {};
      for (const [k, v] of Object.entries(raw)) {
        absolute[k] = v.startsWith("http") ? v : `${base}${v}`;
      }
      setMedia(absolute);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-32 md:py-40" style={{ background: "var(--bg)" }} data-testid="service-cards">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-20"
        >
          <div className="md:col-span-2 overline pt-2">Catalogue</div>
          <div className="md:col-span-10">
            <h2 className="font-display text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em", lineHeight: 0.95 }}>
              Every discipline, <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>one crew.</span>
            </h2>
            <p className="mt-8 font-body text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
              Ten services. One accountable partner. Click any card to open the full file — equipment, method, timeline.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => <ServiceCard key={s.id} s={s} i={i} realImage={media[s.id]} />)}
        </div>
      </div>
    </section>
  );
}
